
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationInput from '../LocationInput';
import { Location } from '../../types/maps';
import { Shield, Clock, UserCheck } from 'lucide-react';


const HeroSection = () => {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState<Location | null>(null);
  const [destination, setDestination] = useState<Location | null>(null);

  const handleBookNow = () => {
    navigate('/reservation', { state: { pickup, destination } });
  };

  return (
    <div className="relative min-h-screen flex items-center">
      {/* Elegant Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/80 via-secondary/70 to-secondary/60 z-10" />
      <div className="absolute inset-0">
        <img
          src="/chauffeur-prive-dans-le-monde-scaled-1-e1723452158165.jpg"
          alt="Luxury chauffeur service with premium vehicles in Brussels"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </div>

      <div className="relative z-20 container mx-auto px-4 py-32">
        <div className="max-w-5xl mx-auto text-center mb-16 animate-fade-in">
          <div className="inline-block mb-6">
            <span className="text-primary text-sm font-semibold uppercase tracking-luxury">
              Premium Chauffeur Service
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 text-white leading-tight">
            Experience Unparalleled
            <span className="block text-primary mt-2">Luxury & Discretion</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto font-light">
            Brussels' most trusted executive transportation service. Available 24/7 for airport transfers, business travel, and special occasions.
          </p>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-8 mb-16 text-sm">
            {[
              { icon: Clock, text: "24/7 Premium Service" },
              { icon: Shield, text: "Fully Licensed & Insured" },
              { icon: UserCheck, text: "Expert Chauffeurs" }
            ].map(({ icon: Icon, text }, index) => (
              <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full">
                <Icon className="w-5 h-5 text-primary" />
                <span className="text-white font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Prominent Booking Form */}
        <div className="max-w-4xl mx-auto">
          <div className="glass-effect p-8 md:p-10 rounded-2xl space-y-6 animate-slide-up">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-serif font-semibold text-secondary mb-2">
                Book Your Luxury Ride
              </h2>
              <p className="text-gray-600">Instant confirmation • Free cancellation up to 24h</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LocationInput
                label="Pickup Location"
                value={pickup?.address || ''}
                onChange={setPickup}
              />
              <LocationInput
                label="Destination"
                value={destination?.address || ''}
                onChange={setDestination}
              />
            </div>
            
            <button
              onClick={handleBookNow}
              className="btn-luxury w-full text-base py-4"
            >
              Reserve Now
            </button>
            
            <p className="text-center text-sm text-gray-500">
              No credit card required • Pay after your ride
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
